// Class-level TSIA2 report: one test, every student in the class.
//
// ClassSHSATReport with the timing removed and percentages in place of raw
// counts, so a tutor moving between test types sees the same page: the roster
// with per-section and total scores and a progress bar, the score
// distribution chart, then the per-question response grid with the question
// popup and the spreadsheet download.
//
// The batch print differs from the SHSAT page on purpose. That page carries a
// second, hand-written copy of the student report for paper, fed by its own
// endpoint, and the two copies have drifted before. Here the batch mounts the
// real TestTSIA2Report once per finished student (it accepts a test id, an
// embedded flag and a ready callback for exactly this) inside the same
// container the Students page's print sheet uses, and prints under that
// sheet's body class. One report component, one stylesheet, three ways to
// reach the printer.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoPrintOutline } from 'react-icons/io5';
import { MdOutlinePerson, MdOutlinePersonOff } from 'react-icons/md';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Spinner from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import Table from 'components/Table';
import Dropdown from 'components/Dropdown2';
import Dropdown4 from 'components/Dropdown4';
import Loader from 'components/Loader';
import QuestionPopup from 'components/QuestionPopup';
import TestTSIA2Report from 'pages/TestTSIA2Report';

// The bulk-print rules (one report per sheet, app shell removed) key off the
// `printing-bulk-reports` body class and the .bulkPrint__* containers used
// below. Imported here so this page prints correctly even when the Students
// page's print sheet has never been visited in this session.
import 'pages/BulkPrintReports/index.css';
import './index.css';

type SectionScore = {
    subject: string;
    score: number | null;
    rawCorrect: number | null;
    questionCount: number | null;
    stages: string[];
};

type StudentRow = {
    firstName: string;
    lastName: string;
    studentId: number | null;
    test: {
        testId: string | null;
        score: number | null;
        rawCorrect: number | null;
        questionCount: number | null;
        testSections: SectionScore[];
        completedModuleCount: number;
        testModuleCount: number;
        inProgress: boolean;
    };
    moduleAnswers: {
        subject: string;
        moduleNumber: number;
        moduleName: string;
        answers: { questionId: string | null; questionType: number; answer: string | null; isCorrect: boolean }[];
    }[];
};

type ClassReport = {
    testName: string;
    testType: string;
    className: string;
    classTestSections: string[];
    configTestSections: string[];
    isConfigured: boolean;
    studentScores: number[];
    sectionScores: Record<string, number[]>;
    avgScore: number | string;
    modules: { subject: string; moduleNumber: number; moduleName: string; questions: [number, string][] }[];
    students: StudentRow[];
};

// Percent buckets for the distribution chart. TSIA2 has no scaled score, so
// the axis is the flat percent the report shows.
const PERCENT_RANGES = [
    { label: '< 50', min: 0, max: 50 },
    { label: '50 - 59', min: 50, max: 60 },
    { label: '60 - 69', min: 60, max: 70 },
    { label: '70 - 79', min: 70, max: 80 },
    { label: '80 - 89', min: 80, max: 90 },
    { label: '90 - 100', min: 90, max: 101 },
];

const getScoreDistribution = (scores: number[]) => {
    const bins = PERCENT_RANGES.map(range => ({ range: range.label, students: 0 }));
    scores.filter(score => score !== null && score !== undefined).forEach(score => {
        const index = PERCENT_RANGES.findIndex(range => score >= range.min && score < range.max);
        if (index >= 0) bins[index].students += 1;
    });
    return bins;
};

const percentCell = (score: number | null, correct: number | null, total: number | null) => (
    score === null || score === undefined
        ? null
        : (
            <span className='classTSIA2Report__cellCenter'>
                <span className='classTSIA2Report__percent'>{Math.round(score)}%</span>
                {correct !== null && total !== null && (
                    <span className='classTSIA2Report__raw'>{correct}/{total}</span>
                )}
            </span>
        )
);

function ClassTSIA2Report() {
    const [testData, setTestData] = useState<ClassReport | null>(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [subjectIndex, setSubjectIndex] = useState(0);
    const [hideStudentNames, setHideStudentNames] = useState(false);
    const [downloadInProgress, setDownloadInProgress] = useState(false);

    // Question popup, as on the other class reports.
    const [isQuestionPopupOpen, setIsQuestionPopupOpen] = useState(false);
    const [questionId, setQuestionId] = useState<string | null>(null);
    const [correctStudent, setCorrectStudent] = useState<number | null>(0);
    const [currentQuestionData, setCurrentQuestionData] = useState(null);

    // Batch print: the ids being printed (null = not printing) and the ones
    // whose report has its data. A Set, not a counter, so a report that
    // remounts cannot count itself twice.
    const [batchIds, setBatchIds] = useState<string[] | null>(null);
    const [readyIds, setReadyIds] = useState(new Set<string>());
    const batchRef = useRef<HTMLDivElement>(null);

    const { classId, testId } = useParams();
    const navigate = useNavigate();
    const questionPopupRef = useOutsideClickHandler(() => handleExit(false));

    useEffect(() => {
        axios.get(`tutors/classes/${classId}/digital-tsia2/report/${testId}/`)
            .then(res => setTestData(res.data))
            .catch(error => console.log(error));
    }, [classId, testId]);

    // --- score distribution ----------------------------------------------

    const subjects = testData ? ['Total', ...testData.configTestSections] : [];
    const selectedSubject = subjects[subjectIndex] ?? 'Total';
    const selectedScores = testData
        ? (selectedSubject === 'Total'
            ? testData.studentScores
            : (testData.sectionScores[selectedSubject] ?? []))
        : [];
    const scoreChartData = testData ? getScoreDistribution(selectedScores) : [];
    // The average follows the dropdown: the whole test, or one section.
    const avgScore = selectedSubject === 'Total'
        ? testData?.avgScore ?? '-'
        : selectedScores.length
            ? Math.round((selectedScores.reduce((sum, score) => sum + score, 0) / selectedScores.length) * 10) / 10
            : '-';

    // --- question popup --------------------------------------------------

    const handleExit = (isExit: boolean) => {
        setIsQuestionPopupOpen(isExit);
        setCurrentQuestionData(null);
    };

    const handleQuestionPopup = (id: string) => {
        setQuestionId(id);
        axios.get(`tutors/classes/report/question/${id}/${testId}/`)
            .then(res => setCurrentQuestionData(res.data))
            .catch(error => console.log(error));
        setIsQuestionPopupOpen(true);
    };

    useEffect(() => {
        if (!testData || !questionId) return;
        let correctCount = 0;
        let taken = 0;
        testData.students.forEach(student => {
            student.moduleAnswers.forEach(moduleAnswer => {
                const answer = moduleAnswer.answers.find(a => a.questionId === questionId);
                if (answer) {
                    taken += 1;
                    if (answer.isCorrect) correctCount += 1;
                }
            });
        });
        setCorrectStudent(taken > 0 ? (correctCount / taken) * 100 : null);
    }, [testData, questionId]);

    // --- download --------------------------------------------------------

    const exportStudentTestData = () => {
        setDownloadInProgress(true);
        axios({
            url: `tutors/report/download/${classId}/${testId}/`,
            method: 'GET',
            responseType: 'blob',
        })
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${testData?.className}-${testData?.testName}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setDownloadInProgress(false);
            })
            .catch(error => {
                console.error('Error downloading report:', error);
                setDownloadInProgress(false);
            });
    };

    // --- batch print -----------------------------------------------------

    // Only finished attempts have a report to print. With none, the button is
    // disabled rather than opening a print dialog on an empty sheet.
    const finishedIds = (testData?.students ?? [])
        .filter(student => student.test.score !== null && student.test.testId)
        .map(student => student.test.testId as string);

    const printBatchReport = () => {
        if (!testData || batchIds || finishedIds.length === 0) return;
        setReadyIds(new Set<string>());
        setBatchIds(finishedIds);
    };

    // Stable per test id, so a report's fetch effect is not retriggered by
    // this page re-rendering as its siblings arrive.
    const markReady = useCallback((id: string) => () => {
        setReadyIds(previous => {
            if (previous.has(id)) return previous;
            const next = new Set(previous);
            next.add(id);
            return next;
        });
    }, []);

    const allReady = batchIds !== null && readyIds.size >= batchIds.length;

    useEffect(() => {
        if (!allReady) return;
        let cancelled = false;

        // Wait for what renders after the data: organization logos and
        // webfonts. Capped, so a wedged asset still yields a printable sheet.
        const imagesSettled = () => {
            const root = batchRef.current;
            if (!root) return Promise.resolve();
            const pending = Array.from(root.querySelectorAll('img'))
                .filter((image: HTMLImageElement) => !image.complete)
                .map((image: HTMLImageElement) => new Promise(resolve => {
                    image.addEventListener('load', resolve, { once: true });
                    image.addEventListener('error', resolve, { once: true });
                }));
            return Promise.all(pending);
        };
        const fontsSettled = (document as any).fonts?.ready ?? Promise.resolve();
        const capped = Promise.race([
            Promise.all([imagesSettled(), fontsSettled]),
            new Promise(resolve => window.setTimeout(resolve, 8000)),
        ]);

        capped.then(() => {
            if (cancelled) return;
            // The same body class and @page override the Students page's print
            // sheet uses, so the bulk-print stylesheet lays out the pages.
            document.body.classList.add('printing-bulk-reports');
            const pageStyle = document.createElement('style');
            pageStyle.textContent = '@page { margin: 0; }';
            document.head.appendChild(pageStyle);
            const cleanup = () => {
                document.body.classList.remove('printing-bulk-reports');
                pageStyle.remove();
                window.removeEventListener('afterprint', cleanup);
                // Unmount the batch so the page is not carrying a report per
                // student for the rest of the visit.
                setBatchIds(null);
            };
            window.addEventListener('afterprint', cleanup);
            window.print();
        });

        return () => { cancelled = true; };
    }, [allReady]);

    if (testData === null) return <Loader />;

    const activeModule = testData.modules[moduleIndex] ?? testData.modules[0];
    const moduleLabels = testData.modules.map(module => module.moduleName);
    const answersFor = (student: StudentRow) => student.moduleAnswers.find(moduleAnswer => (
        moduleAnswer.subject === activeModule.subject
        && moduleAnswer.moduleNumber === activeModule.moduleNumber
    ));
    // Only students who were served this stage appear under it: a student
    // who met the CRC cutoff has no Diagnostic row to show.
    const studentsOnModule = testData.students.filter(student => answersFor(student));

    return (
        <div className='classTSIA2Report'>
            <Helmet>
                <title>Class Report - Test Center Admin</title>
            </Helmet>
            <div className='classTSIA2Report__screen'>
                {isQuestionPopupOpen &&
                    <QuestionPopup
                        info={currentQuestionData?.info}
                        correctStudent={correctStudent !== null ? `${correctStudent.toFixed(1)}%` : '-'}
                        testName={testData?.testName}
                        question={currentQuestionData?.question}
                        passages={currentQuestionData?.passages}
                        figures={currentQuestionData?.figures}
                        choices={currentQuestionData?.choices}
                        hotText={currentQuestionData?.hotText}
                        buckets={currentQuestionData?.buckets}
                        dragAndDropItems={currentQuestionData?.dragAndDropItems}
                        tableGridRows={currentQuestionData?.tableGridRows}
                        tableGridColumns={currentQuestionData?.tableGridColumns}
                        inlineChoices={currentQuestionData?.inlineChoices}
                        questionHelper={currentQuestionData?.questionHelper}
                        correctChoice={currentQuestionData?.correctChoice}
                        correctAnswers={currentQuestionData?.correctAnswers}
                        manageExit={() => handleExit(false)}
                        innerRef={questionPopupRef}
                    />
                }
                <Header
                    title={testData.testName}
                    prevRoutes={[{ name: testData.className, path: `/classes/${classId}` }]}
                />

                <div className='classTSIA2Report__subHeaderRow'>
                    <h2 className='classTSIA2Report__subHeader'>Scores by students</h2>
                    <div className='classTSIA2Report__buttonGroup'>
                        <Button className='classTSIA2Report__button' secondary onClick={() => setHideStudentNames(!hideStudentNames)}>
                            {hideStudentNames
                                ? <><MdOutlinePerson className='classTSIA2Report__buttonIcon' /><p>Show</p></>
                                : <><MdOutlinePersonOff className='classTSIA2Report__buttonIcon' /><p>Hide</p></>
                            }
                        </Button>
                        <Button className='classTSIA2Report__button' onClick={printBatchReport} disabled={finishedIds.length === 0}>
                            {batchIds !== null
                                ? <img src={Spinner} />
                                : <><IoPrintOutline className='classTSIA2Report__buttonIcon' /><p>Report</p></>
                            }
                        </Button>
                    </div>
                </div>

                <div className='classTSIA2Report__scoresContainer'>
                    <Table
                        className='classTSIA2Report__table'
                        header={[
                            'Students',
                            ...testData.classTestSections.map(subject => (
                                <p className='classTSIA2Report__cellCenter'>{subject}</p>
                            )),
                            <p className='classTSIA2Report__cellCenter'>Score</p>,
                            <p className='classTSIA2Report__cellCenter'>Progress</p>,
                        ]}
                        columnTypes={[
                            'string',
                            ...Array(testData.classTestSections.length).fill('number'),
                            'number',
                            null,
                        ]}
                        sortValues={testData.students.map(student => [
                            `${student.lastName} ${student.firstName}`,
                            ...testData.classTestSections.map(subject => (
                                student.test.testSections.find(section => section.subject === subject)?.score ?? null
                            )),
                            student.test.score,
                            null,
                        ])}
                        rows={testData.students.map(student => [
                            hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                            ...testData.classTestSections.map(subject => {
                                const section = student.test.testSections.find(s => s.subject === subject);
                                return section
                                    ? percentCell(section.score, section.rawCorrect, section.questionCount)
                                    : null;
                            }),
                            percentCell(student.test.score, student.test.rawCorrect, student.test.questionCount),
                            <div className='classTSIA2Report__progress classTSIA2Report__cellCenter'>
                                {new Array(student.test.testModuleCount).fill(null).map((_count, c) => (
                                    <div className={student.test.completedModuleCount > c
                                        ? 'classTSIA2Report__progressBar--filled'
                                        : (student.test.inProgress && c === 0)
                                        ? 'classTSIA2Report__progressBar--started'
                                        : 'classTSIA2Report__progressBar'
                                    } key={c}></div>
                                ))}
                            </div>,
                        ])}
                        onSelectCell={testData.students.map(student => [
                            hideStudentNames || !student.studentId ? null : () => navigate(`/students/${student.studentId}`),
                            ...testData.classTestSections.map(() => null),
                            student.test.score !== null && student.test.testId
                                ? () => navigate(`/report/digital-tsia2/${student.test.testId}`)
                                : null,
                            null,
                        ])}
                        navigator={false}
                    />
                    <div className='classTSIA2Report__scoreDistribution'>
                        <div className='classTSIA2Report__scoreDistribution__header'>
                            <div className='classTSIA2Report__scoreDistribution__title'>Score distribution</div>
                            <Dropdown4
                                width={210}
                                className='classTSIA2Report__dropdown'
                                data={subjects}
                                onSelect={value => setSubjectIndex(subjects.findIndex(subject => subject === value))}
                                value={selectedSubject}
                            />
                        </div>
                        <div className='classTSIA2Report__scoreDistribution__content'>
                            <div className='classTSIA2Report__scoreDistribution__graph'>
                                <ResponsiveContainer>
                                    <BarChart data={scoreChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                                        <XAxis
                                            dataKey='range'
                                            tick={{ fontSize: 12 }}
                                            axisLine={false}
                                            tickLine={false}
                                            label={{
                                                value: 'Percent correct',
                                                position: 'insideBottom',
                                                offset: -10,
                                                style: { fontSize: 12, fill: '#6B7280', fontWeight: 500 },
                                            }}
                                        />
                                        <YAxis
                                            allowDecimals={false}
                                            domain={[0, 'dataMax + 1']}
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 12 }}
                                        />
                                        <Tooltip />
                                        <Bar dataKey='students' fill='#4F46E5' radius={[4, 4, 0, 0]} minPointSize={2} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className='classTSIA2Report__scoreDistribution__avgScore'>
                                <div className='classTSIA2Report__scoreDistribution__avg'>Avg Score</div>
                                <div className='classTSIA2Report__scoreDistribution__avgScoreNum'>
                                    {typeof avgScore === 'number' ? `${avgScore}%` : avgScore}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='classTSIA2Report__subHeaderRow'>
                    <h2 className='classTSIA2Report__subHeader'>Student responses</h2>
                    <Dropdown
                        className='classTSIA2Report__dropdown'
                        data={moduleLabels}
                        onSelect={value => setModuleIndex(moduleLabels.findIndex(label => label === value))}
                        value={moduleLabels[moduleIndex]}
                    />
                    <div className='classTSIA2Report__buttonGroup'>
                        <Button className='classTSIA2Report__button' onClick={exportStudentTestData}>
                            {downloadInProgress ? <img src={Spinner} /> : 'Download'}
                        </Button>
                    </div>
                </div>
                {activeModule && (
                    <Table
                        header={['Students', ...activeModule.questions.map(question => (
                            <p
                                className='classTSIA2Report__cellCenter classTSIA2Report__response classTSIA2Report__cursor classTSIA2Report__tableHeader'
                                onClick={() => handleQuestionPopup(question[1])}
                            >
                                Q{question[0]}
                            </p>
                        ))]}
                        rows={[
                            [
                                <p className='classTSIA2Report__header'># Incorrect</p>,
                                ...activeModule.questions.map((_question, q) => (
                                    <p className='classTSIA2Report__header classTSIA2Report__cellCenter'>
                                        {studentsOnModule.filter(student => !answersFor(student)?.answers?.[q]?.isCorrect).length}
                                    </p>
                                )),
                            ],
                            ...studentsOnModule.map(student => [
                                hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                                ...(answersFor(student)?.answers ?? []).map(answer => (
                                    <p className={`classTSIA2Report__cellCenter classTSIA2Report__response ${!answer?.isCorrect && (answer?.answer
                                        ? 'classTSIA2Report__cellWrong'
                                        : 'classTSIA2Report__cellSkipped'
                                    )}`}>
                                        {answer?.answer ?? '-'}
                                    </p>
                                )),
                            ]),
                        ]}
                        navigator={false}
                    />
                )}
            </div>

            {/* Printed, never shown: one real TestTSIA2Report per finished
                student, in the bulk-print sheet's containers. */}
            {batchIds !== null && (
                <div className='classTSIA2Report__batch bulkPrint' ref={batchRef}>
                    <div className='bulkPrint__reports'>
                        {batchIds.map(id => (
                            <div className='bulkPrint__report' key={id}>
                                <TestTSIA2Report testIdOverride={id} embedded onReady={markReady(id)} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClassTSIA2Report;
